import { useEffect, useRef, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { Answers, Brand, Locale, Page } from "../../context/AppProvider";
import { useAppContext } from "../../hooks/useAppContext";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { Transition } from "@headlessui/react";
import { Helmet } from "react-helmet-async";
import clo from "../../assets/clo.png";
import closet from "../../assets/closet.png";
import md from "../../assets/md.png";
import { getChatHistory } from "../../api/chatAPI";
import {
  ChatHistory,
  ConversationGroup,
  IPGeolocation,
} from "../../api/models";
import SideBarHistory from "../../components/SideBarHistory";
import Divider from "../../components/ui/Divider";
import ModalClearChat from "../../components/ModalClearChat";
import Modal from "../../components/ui/Modal";
import useBoundStore from "../../store/useBoundStore";
import ModalSettings from "../../components/ModalSettings";
import UserProfile from "../../components/SVGIcons/UserProfile";
import Warning from "../../components/Warning";
import Toast from "../../components/ui/Toast";
import useWindowWidth from "../../hooks/useWindowWidth";

const REFRESH_TOKEN_COOKIE_NAMES = ["www_crt", "test_crt"] as const;

const Layout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    brand,
    setBrand,
    locale,
    setLocale,
    setIPGeoLocation,
    theme,
    setTheme,
  } = useAppContext();
  const user = useBoundStore((state) => state.user);
  const setUser = useBoundStore((state) => state.setUser);
  const getUser = useBoundStore((state) => state.executeGetUser);
  const answers = useBoundStore((state) => state.answers);
  const setCitation = useBoundStore((state) => state.setCitation);
  const setPage = useBoundStore((state) => state.setPage);
  const setAnswers = useBoundStore((state) => state.setAnswer);
  const isLoading = useBoundStore((state) => state.isLoading);
  const error = useBoundStore((state) => state.error);
  const setError = useBoundStore((state) => state.setError);
  const setConversationID = useBoundStore((state) => state.setId);
  const lastQuestion = useBoundStore((state) => state.lastQuestion);
  const setLastQuestion = useBoundStore((state) => state.setLastQuestion);
  const chatHistory = useBoundStore((state) => state.chatHistory);
  const setChatHistory = useBoundStore((state) => state.setChatHistory);
  const setChatHistoryPanel = useBoundStore(
    (state) => state.setChatHistoryPanel,
  );
  const [openClearChatModal, setOpenClearChatModal] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | undefined>(
    undefined,
  );
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<JSX.Element | null>(null);
  const width = useWindowWidth();

  const clearChat = () => {
    console.log("lastQuestion", lastQuestion);

    if (!chatHistory && answers.length > 0 && !openClearChatModal) {
      // Open the clear chat modal
      setOpenClearChatModal(true);
    } else if (lastQuestion && !isLoading) {
      console.log("Clearing chat...");

      setLastQuestion(undefined);
      error && setError({} as AxiosError);
      setCitation(undefined);
      setAnswers([]);
      setPage(Page.HOME);
      setConversationID("");
      setOpenClearChatModal(false);
    }
  };

  const handleChatConversation = async (
    conversationGroup: ConversationGroup,
  ) => {
    let newAnswers = [];
    for (let i = 0; i < conversationGroup.conversations.length; i++) {
      // Set the last question in the context
      setLastQuestion(conversationGroup.conversations[i].question);

      newAnswers.push([
        conversationGroup.conversations[i].question,
        {
          _id: conversationGroup.conversations[i]._id,
          conversation_id: conversationGroup._id,
          answer: conversationGroup.conversations[i].answer,
          follow_up_questions:
            conversationGroup.conversations[i].follow_up_questions,
          citation_articles:
            conversationGroup.conversations[i].citation_articles,
          suggested_articles:
            conversationGroup.conversations[i].suggested_articles,
          suggested_videos: conversationGroup.conversations[i].suggested_videos,
          youtube_links: conversationGroup.conversations[i].youtube_links,
          response_time: conversationGroup.conversations[i].response_time,
        },
        brand,
      ]);
    }

    // Set the answers array in the context
    setAnswers(newAnswers as Answers[]);

    if (width <= 700) {
      setChatHistoryPanel(false);
    }

    // Set the conversation ID in the context
    setConversationID(conversationGroup._id);
  };

  // Handle URL query parameters
  useEffect(() => {
    const { search } = window.location;

    const searchParamsBrand = new URLSearchParams(search)
      .get("brand")
      ?.toLowerCase();
    const searchParamsLocale = new URLSearchParams(search).get(
      "locale",
    ) as Locale;

    if (
      import.meta.env.MODE === "dev" ||
      import.meta.env.MODE === "localhost"
    ) {
      setBrand(
        searchParamsBrand === "allinone"
          ? Brand.ALLINONE
          : searchParamsBrand === "closet"
            ? Brand.CLOSET
            : searchParamsBrand === "closet_connect"
              ? Brand.CLOSET_CONNECT
              : searchParamsBrand === "md"
                ? Brand.MD
                : Brand.CLO3D,
      );
    } else {
      setBrand(
        searchParamsBrand === "closet"
          ? Brand.CLOSET
          : searchParamsBrand === "md"
            ? Brand.MD
            : Brand.CLO3D,
      );
    }

    setLocale(
      Object.values(Locale).includes(searchParamsLocale)
        ? searchParamsLocale
        : locale,
    );

    const newQueryParameters: URLSearchParams = new URLSearchParams();
    newQueryParameters.set("brand", searchParamsBrand || brand.toLowerCase());
    newQueryParameters.set("locale", searchParamsLocale || locale);
    setSearchParams(newQueryParameters);
  }, []);

  useEffect(() => {
    const initializeUI = async () => {
      const hasRefreshTokenCookie = REFRESH_TOKEN_COOKIE_NAMES.some(
        (cookieName) => Cookies.get(cookieName) !== undefined,
      );

      try {
        if (!hasRefreshTokenCookie) {
          setUser(null);
          return;
        }

        // Capture the returned user directly from the action
        const freshUser = await getUser();

        // Use freshUser instead of the state variable 'user'
        if (freshUser?.user_id) {
          setUser(freshUser);
          setIsChatHistoryLoading(true);
          const history = await getChatHistory(brand, freshUser.user_id);
          setChatHistory(history);
        } else {
          setUser(null);
        }
      } catch {
        if (hasRefreshTokenCookie) {
          setToastMessage(
            `Failed to retrieve user information. Please sign in.`,
          );
          setShowToast(true);
        }
      } finally {
        setIsChatHistoryLoading(false);
      }
    };

    initializeUI();
  }, [brand]);

  // Get Country
  useEffect(() => {
    (async () => {
      try {
        // Free IP geolocation service
        const response = await axios.get("https://ipapi.co/json/");
        const data = await response.data;

        if (data) {
          setIPGeoLocation({
            ip: data.ip,
            city: data.city,
            region: data.region,
            region_code: data.region_code,
            country: data.country,
            country_name: data.country_name,
            country_code: data.country_code,
            continent_code: data.continent_code,
            latitude: data.latitude,
            longitude: data.longitude,
            timezone: data.timezone,
          } as IPGeolocation);
        }
      } catch (error) {
        console.error("Error getting IP Geolocation:", error);
      }
    })();

    let theme = localStorage.getItem("theme") || "light";
    document.documentElement.setAttribute("data-theme", theme);
    setTheme(theme);
  }, []);

  useEffect(() => {
    if (error && (error.message || error.response)) {
      setToastMessage(
        `Error ${error.response?.status || ""}: ${error.message}`,
      );
      setShowToast(true);
    }
  }, [error]);

  return (
    <>
      <Helmet>
        <title>
          {brand === Brand.CLOSET
            ? "CLO-SET AI Chatbot"
            : brand === Brand.CLOSET_CONNECT
              ? "CONNECT AI Chatbot"
              : brand === Brand.MD
                ? "Marvelous Design AI Chatbot"
                : "CLO3D AI Chatbot"}
        </title>
        <link
          id="favicon"
          rel="icon"
          type="image"
          href={brand === Brand.CLOSET ? closet : brand === Brand.MD ? md : clo}
        />
      </Helmet>
      <div
        className={`flex h-full w-full ${theme === "dark" ? "bg-grayscale-solid-g1" : "bg-grayscale-solid-g0"}`}
      >
        {
          <SideBarHistory
            isChatHistoryLoading={isChatHistoryLoading}
            handleChatConversation={handleChatConversation}
            clearChat={clearChat}
          />
        }
        <div className="flex h-full grow flex-col">
          <header className="flex h-[56px] w-full justify-end p-5 sm:px-5 md:h-[60px] md:px-10 md:py-3 lg:px-4">
            <div className="flex items-center gap-x-4">
              {user && (
                <>
                  <span>
                    <span className="text-center text-[12px] font-medium leading-tight text-grayscale-solid-g6 sm:text-[14px] md:inline lg:text-[16px]">
                      Hello,{" "}
                    </span>
                    <span className="text-center text-[12px] font-medium leading-tight text-grayscale-solid-g10 sm:text-[14px] lg:text-[16px]">
                      {user.name}
                    </span>
                  </span>
                  <Divider />
                </>
              )}
              <button
                ref={profileButtonRef}
                onClick={() => {
                  setOpenSettingsModal((prev) => !prev);
                }}
              >
                <UserProfile className="h-7 w-7 cursor-pointer fill-grayscale-solid-g10 hover:fill-blue-solid-blue6" />
              </button>
            </div>
          </header>

          <main
            className={`scrollbar relative h-[calc(100%-56px-110px)] overflow-y-auto overflow-x-hidden px-2 py-3 sm:ml-[60px] sm:px-5 sm:py-10 sm:transition-all sm:duration-[250ms] md:ml-0 md:px-10 lg:px-16 lg:py-4`}
          >
            <Outlet />
          </main>

          {answers.length === 0 && (
            <footer className="flex w-full grow items-end justify-center pb-1">
              <Warning />
            </footer>
          )}
        </div>
      </div>

      <Modal
        className="w-[250px] md:w-[400px]"
        open={openClearChatModal}
        setOpen={setOpenClearChatModal}
      >
        <ModalClearChat clearChat={clearChat} />
      </Modal>

      <Transition
        as={"div"}
        className={
          "absolute right-5 top-[60px] z-[40] w-[calc(100vw-40px)] sm:w-72"
        }
        show={openSettingsModal}
        enter="transition-opacity duration-[250ms]"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-[250ms]"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        unmount={false}
      >
        <ModalSettings
          openModal={openModal}
          setOpenModal={setOpenModal}
          modalContent={modalContent}
          setModalContent={setModalContent}
          openSettingsModal={openSettingsModal}
          setOpenSettingsModal={setOpenSettingsModal}
          triggerRef={profileButtonRef}
        />
      </Transition>

      <Modal className="w-[325px]" open={openModal} setOpen={setOpenModal}>
        {modalContent}
      </Modal>

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        type="warning"
        message={toastMessage}
      />
    </>
  );
};

export default Layout;
